import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, X, FileText, Map, Bike, Loader2 } from 'lucide-react'
import { useLanguageStore } from '../../lib/store'

interface Suggestion {
    id: string | number
    type: 'article' | 'tour' | 'vehicle'
    title_vi: string
    title_en: string
    image: string | null
    url: string
}

interface SearchBarProps {
    placeholder?: string
    className?: string
    inputClassName?: string
    showIcon?: boolean
    autoFocus?: boolean
    onSearch?: (query: string) => void
}

export function SearchBar({
    placeholder,
    className = '',
    inputClassName = '',
    showIcon = true,
    autoFocus = false,
    onSearch
}: SearchBarProps) {
    const navigate = useNavigate()
    const { language } = useLanguageStore()

    const [query, setQuery] = useState('')
    const [suggestions, setSuggestions] = useState<Suggestion[]>([])
    const [isOpen, setIsOpen] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [selectedIndex, setSelectedIndex] = useState(-1)

    const inputRef = useRef<HTMLInputElement>(null)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const debounceRef = useRef<ReturnType<typeof setTimeout>>()

    // Default placeholder
    const defaultPlaceholder = language === 'en' ? 'Search articles, tours, vehicles...' : 'Tìm kiếm bài viết, tour, xe...'

    // Fetch suggestions
    const fetchSuggestions = useCallback(async (searchQuery: string) => {
        if (searchQuery.length < 1) {
            setSuggestions([])
            return
        }

        setIsLoading(true)
        try {
            const res = await fetch(`/api/search/suggest?q=${encodeURIComponent(searchQuery)}&limit=6`)
            const data = await res.json()
            if (data.success) {
                setSuggestions(data.suggestions || [])
            }
        } catch (error) {
            console.error('Search suggest error:', error)
        } finally {
            setIsLoading(false)
        }
    }, [])

    // Debounced search
    useEffect(() => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current)
        }

        if (query.length >= 1) {
            debounceRef.current = setTimeout(() => {
                fetchSuggestions(query)
            }, 300)
        } else {
            setSuggestions([])
        }

        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current)
            }
        }
    }, [query, fetchSuggestions])

    // Handle keyboard navigation
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!isOpen && e.key !== 'Enter') return

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault()
                setSelectedIndex(prev =>
                    prev < suggestions.length - 1 ? prev + 1 : prev
                )
                break
            case 'ArrowUp':
                e.preventDefault()
                setSelectedIndex(prev => prev > 0 ? prev - 1 : -1)
                break
            case 'Enter':
                e.preventDefault()
                if (selectedIndex >= 0 && suggestions[selectedIndex]) {
                    handleSuggestionClick(suggestions[selectedIndex])
                } else if (query.trim()) {
                    handleSearch()
                }
                break
            case 'Escape':
                setIsOpen(false)
                setSelectedIndex(-1)
                inputRef.current?.blur()
                break
        }
    }

    // Navigate to search results
    const handleSearch = () => {
        if (query.trim()) {
            setIsOpen(false)
            if (onSearch) {
                onSearch(query.trim())
            } else {
                navigate(`/search/${encodeURIComponent(query.trim())}`)
            }
        }
    }

    // Handle suggestion click
    const handleSuggestionClick = (suggestion: Suggestion) => {
        setIsOpen(false)
        setQuery('')
        navigate(suggestion.url)
    }

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
                inputRef.current && !inputRef.current.contains(e.target as Node)) {
                setIsOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    // Get type icon
    const getTypeIcon = (type: string) => {
        switch (type) {
            case 'article': return <FileText className="w-3.5 h-3.5 text-blue-500" />
            case 'tour': return <Map className="w-3.5 h-3.5 text-orange-500" />
            case 'vehicle': return <Bike className="w-3.5 h-3.5 text-green-500" />
            default: return null
        }
    }

    // Get type label
    const getTypeLabel = (type: string) => {
        const labels: Record<string, { vi: string; en: string }> = {
            article: { vi: 'Bài viết', en: 'Article' },
            tour: { vi: 'Tour', en: 'Tour' },
            vehicle: { vi: 'Xe', en: 'Vehicle' }
        }
        return labels[type]?.[language] || type
    }

    return (
        <div className={`relative ${className}`}>
            {/* Input */}
            <div className="relative">
                {showIcon && (
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                )}
                <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={(e) => {
                        setQuery(e.target.value)
                        setIsOpen(true)
                        setSelectedIndex(-1)
                    }}
                    onFocus={() => query.length >= 1 && setIsOpen(true)}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder || defaultPlaceholder}
                    autoFocus={autoFocus}
                    className={`w-full ${showIcon ? 'pl-10' : 'pl-4'} pr-10 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition ${inputClassName}`}
                />
                {query && (
                    <button
                        onClick={() => {
                            setQuery('')
                            setSuggestions([])
                            inputRef.current?.focus()
                        }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                        <X className="w-4 h-4" />
                    </button>
                )}
            </div>

            {/* Dropdown */}
            {isOpen && (query.length >= 1) && (
                <div
                    ref={dropdownRef}
                    className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-xl border z-50 overflow-hidden"
                >
                    {isLoading ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="w-5 h-5 text-primary-600 animate-spin" />
                        </div>
                    ) : suggestions.length > 0 ? (
                        <div className="max-h-[400px] overflow-y-auto">
                            {suggestions.map((suggestion, index) => (
                                <button
                                    key={`${suggestion.type}-${suggestion.id}`}
                                    onClick={() => handleSuggestionClick(suggestion)}
                                    className={`w-full flex items-center gap-3 p-3 text-left hover:bg-gray-50 transition ${index === selectedIndex ? 'bg-primary-50' : ''
                                        }`}
                                >
                                    {/* Image */}
                                    <div className="w-12 h-12 md:w-14 md:h-14 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                                        {suggestion.image ? (
                                            <img
                                                src={suggestion.image}
                                                alt=""
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-gray-300">
                                                {getTypeIcon(suggestion.type)}
                                            </div>
                                        )}
                                    </div>

                                    {/* Content */}
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium text-sm line-clamp-2 text-gray-900">
                                            {language === 'en' ? suggestion.title_en : suggestion.title_vi}
                                        </p>
                                        <span className="inline-flex items-center gap-1 text-xs text-gray-500 mt-0.5">
                                            {getTypeIcon(suggestion.type)}
                                            {getTypeLabel(suggestion.type)}
                                        </span>
                                    </div>
                                </button>
                            ))}

                            {/* View all results */}
                            <button
                                onClick={handleSearch}
                                className="w-full p-3 text-center text-sm font-medium text-primary-600 hover:bg-primary-50 border-t transition"
                            >
                                {language === 'en'
                                    ? `View all results for "${query}"`
                                    : `Xem tất cả kết quả cho "${query}"`
                                }
                            </button>
                        </div>
                    ) : (
                        <div className="py-8 text-center text-gray-500 text-sm">
                            {language === 'en' ? 'No results found' : 'Không tìm thấy kết quả'}
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
